import React, { useState, useReducer, useEffect, useRef } from 'react'
import { lockedReducer } from '../../../utils/defaultReducer'

import { Grid, Box, Tooltip } from '@mui/material'
import api from '../../../api'
import { getSession } from '../../../auth'
import FinishedRow from './finishedrow'
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer"
import useNotification from '../../Notification'

const limit = 6

const REQUEST_LOCK_KEY = `finishedv2_lock`
const REQUEST_COOLDOWN = 30000

const getData = async (
    state,
    setState,
    between = 0,
    filterSettings = null,
    filtered = false,
    setFilterSettings = () => {},
    supervisedQueues = [],
    classificationFilter = null,
    classified = null,
    setClassified = () => {},
    fetchingRef,
) => {
    if (fetchingRef.current) return
    
    const lastRequest = Number(localStorage.getItem(REQUEST_LOCK_KEY) || 0)

    if (Date.now() - lastRequest < REQUEST_COOLDOWN) {
        return
    }
    
    const enterpriseId = getSession()?.profile.enterprise.id
    
    if (! enterpriseId)
        return

    fetchingRef.current = true

    localStorage.setItem(REQUEST_LOCK_KEY, Date.now().toString())

    let requestSucceeded = false

    try {
        let concaturl = ""

        if (between > 0)
            concaturl = `&from=${state.cursorup}&to=${between}`

        if (classificationFilter !== null) {
            const res = await api.get(`api/enterprises/${enterpriseId}/calls/chat/classified_finished/?limit=${limit}&cursor=${classified.cursor}&classification=${classificationFilter}${concaturl}`)
            if (res.status >= 200 && res.status < 300) {
                const actual_ids = new Set(classified.rows.map((each) => each.id))
                const rows = [
                    ...classified.rows,
                    ...res.data.rows.filter((each) => !actual_ids.has(each.id))
                ]

                setClassified({
                    rows: rows.toSorted((x, y) => y.last_msg_id - x.last_msg_id),
                    cursor: res.data.cursor || 0,
                    cursorup: res.data.cursorup > 0 ? res.data.cursorup : classified.cursorup
                })

                requestSucceeded = true
            }
            return
        }

        if (filterSettings)
            concaturl = concaturl.concat(filterSettings.data)

        if (filtered)
            concaturl = concaturl.concat('&filtered=true')

        let url = 'api/enterprises/' + enterpriseId + `/calls/chat/finishedv2/?manage=True&limit=${limit}&cursor=${state.cursor}${concaturl}`
        if (supervisedQueues && supervisedQueues.length > 0)
            url += `&queues=${supervisedQueues.join(",")}`

        const res = await api.get(url)
        if (res.status >= 200 && res.status < 300) {
            const actual_ids = new Set(state.rows.map((each) => each.id))
            const rows = [
                ...state.rows,
                ...res.data.rows.filter((each) => !actual_ids.has(each.id))
            ]

            setState({
                rows: rows.toSorted((x, y) => y.last_msg_id - x.last_msg_id),
                cursor: res.data.cursor || 0,
                cursorup: res.data.cursorup > 0 ? res.data.cursorup : state.cursorup
            })

            requestSucceeded = true
        } else {
            if (filterSettings) {
                setFilterSettings(null)
                useNotification(
                    'Informação',
                    `Seus filtros foram desabilitados para melhorar o tempo de resposta.`,
                    'info'
                )
            }
        }
    } finally {
        fetchingRef.current = false

        if (requestSucceeded) {
            localStorage.removeItem(REQUEST_LOCK_KEY)
        }
    }
}


const initialState = {
    rows: [],
    cursor: 0,
    cursorup: 0
}


const renderRow = (props, rows, scrollArea, onClickItem, onCtrlClickItem, isManageCalls, activeChat, state, loadMore, selectedChats=[]) => {
    const { index, style } = props[0];

    if (index >= rows.length) {
        return (
            <FinishedRow
                style={style}
                shouldAlwaysCheck={true}
                key={`individual-finished-row-loader`}
                isVisibleHook={loadMore}
                parent={scrollArea}
                data={{ id: -100 }}
            />
        )
    }

    const row = rows[index]

    return (
        <>
            {row ?
                <FinishedRow
                    style={style}
                    key={`individual-finished-row${rows[index]?.id}`}
                    parent={scrollArea}
                    data={rows[index]}
                    onClickItem={onClickItem}
                    onCtrlClickItem={onCtrlClickItem}
                    isManageCalls={isManageCalls}
                    activeChat={activeChat}
                    selectedChats={selectedChats}
                />
                :
                <></>
            }
        </>
    )
}

const FinishedQueueContainer = ({
    onClickItem,
    isManageCalls,
    activeChat,
    queueAmount,
    filterSettings,
    setFilterSettings,
    disabled,
    classificationFilter=null,
    onCtrlClickItem=()=>{},
    selectedChats=[],
    supervisedQueues=[]
}) => {
    const scrollArea = useRef()
    const fetchingRef = useRef(false)

    const [state, setState] = useReducer(lockedReducer, initialState)

    const [ classified, setClassified ] = useState({ rows: [], cursor: 0, cursorup: 0 })

    const [ prevClassificationFilter, setPrevClassificationFilter ] = useState(null)

    useEffect(() => {
        state.rows = []
        state.cursor = 0
        state.cursorup = 0
        getData(state, setState, 0, filterSettings, !isManageCalls, setFilterSettings, supervisedQueues, null, null, () => {}, fetchingRef)
    }, [filterSettings])

    useEffect(() => {
        if (classificationFilter === null && queueAmount > state.cursorup && state.cursorup > 0 && queueAmount > 0)
            getData(state, setState, queueAmount, filterSettings, !isManageCalls, setFilterSettings, supervisedQueues, null, null, () => {}, fetchingRef)
    }, [ queueAmount, state, filterSettings ])

    useEffect(() => {
        if (classificationFilter !== null) {
            const resetClassified = { rows: [], cursor: 0, cursorup: 0 }
            setClassified(resetClassified)
            getData(state, setState, 0, null, false, () => {}, supervisedQueues, classificationFilter, resetClassified, setClassified, fetchingRef)
        } else if (prevClassificationFilter !== null && classificationFilter === null) {
            setClassified({ rows: [], cursor: 0, cursorup: 0 })
            getData(state, setState, queueAmount, filterSettings, !isManageCalls, setFilterSettings, supervisedQueues, null, null, () => {}, fetchingRef)
        }
        setPrevClassificationFilter(classificationFilter)
    }, [ classificationFilter ])

    return (
        <>
            <Tooltip title={disabled ? 'Filtros não se aplicam à coluna de finalizados.' : ''}>
                {
                    !isManageCalls ?
                        <Box className={`fit-tab v2 nomaxheight ${disabled ? 'disabled' : ''}`} ref={scrollArea} key={`mainWindow${filterSettings}`} >
                            <AutoSizer >
                                {({ height, width }) =>
                                (
                                    <>
                                        <FixedSizeList
                                            height={height}
                                            width={width}
                                            itemSize={100}
                                            itemCount={((classificationFilter === null) ? state.rows.length : classified.rows.length) + ((((classificationFilter === null) ? state.cursor : classified.cursor) !== 0) ? 1 : 0)}
                                            overscanCount={5}
                                        >
                                            {(...props) => renderRow(
                                                props,
                                                (classificationFilter === null) ? state.rows : classified.rows,
                                                scrollArea,
                                                onClickItem,
                                                onCtrlClickItem,
                                                isManageCalls,
                                                activeChat,
                                                (classificationFilter === null) ? state : classified,
                                                (v) => { v 
                                                    ? (classificationFilter === null)
                                                        ? getData(state, setState, 0, filterSettings, !isManageCalls, () => {}, supervisedQueues, null, null, () => {}, fetchingRef)
                                                        : getData(state, setState, 0, null, false, () => {}, supervisedQueues, classificationFilter, classified, setClassified, fetchingRef)
                                                    : null 
                                                },
                                                selectedChats
                                            )}
                                        </FixedSizeList>
                                    </>
                                )}
                            </AutoSizer>
                        </Box >
                        :
                        <Grid item lg={3} md={3} sm={3} xs={3} sx={{ minWidth: '25rem' }}>
                            <Box className={`call-columns queue-chats v2 ${disabled ? 'disabled' : ''}`} >
                                <Box className="column-header" >
                                    <Box className="column-title">
                                        Finalizados
                                    </Box>
                                </Box>
                                <Box className={'fit-tab v2 auto'} ref={scrollArea} key={`mainWindow${filterSettings}`}>
                                    <AutoSizer >
                                        {({ height, width }) =>
                                        (
                                            <>
                                                <FixedSizeList
                                                    height={height}
                                                    width={width}
                                                    itemSize={100}
                                                    itemCount={state.rows.length + ((state.cursor !== 0) ? 1 : 0)}
                                                    overscanCount={5}
                                                >
                                                    {(...props) => renderRow(
                                                        props,
                                                        state.rows,
                                                        scrollArea,
                                                        onClickItem,
                                                        onCtrlClickItem,
                                                        isManageCalls,
                                                        activeChat,
                                                        state,
                                                        (v) => { v ? getData(state, setState, 0, filterSettings, !isManageCalls, () => {}, supervisedQueues, null, null, () => {}, fetchingRef) : null },
                                                        selectedChats
                                                    )}
                                                </FixedSizeList>
                                            </>
                                        )}
                                    </AutoSizer>
                                </Box>
                            </Box>
                        </Grid >
                }
            </Tooltip>
        </>

    )
}

export default FinishedQueueContainer